// Lion Auto's listing table helpers: Compare's section filter, the comp key
// the server's matcher gives every market listing, the table's market rows
// (the group on screen's own match result, else the page's payload), what
// that result says about each row, and the zone fallback in words (a group
// with no comp in its sections priced off its sections' Skybox zone).
// Pure; tested in lionAutoListings.test.js.

/**
 * Compare's section filter, rule for rule (Compare.js getFilteredListings):
 * a listing matches a selected section by sectionId; a listing without one
 * (unmapped in Skybox, sectionId 0) falls back to an exact, case-insensitive
 * display-name match. No selection = the whole venue.
 * @param {object} listing            transformPricing row (own or comp)
 * @param {{id, name}[]} selectedSections  map sections (string ids)
 */
export function matchesSelectedSection(listing, selectedSections) {
  if (!selectedSections || selectedSections.length === 0) return true;
  return selectedSections.some(s => {
    if (listing.sectionId && s.id && String(listing.sectionId) === String(s.id)) return true;
    if (!listing.sectionId || listing.sectionId === 0) {
      const listingName = (listing.displayName || listing.section || '').toLowerCase().trim();
      const selectedName = (s.name || '').toLowerCase().trim();
      if (listingName && selectedName && listingName === selectedName) return true;
    }
    return false;
  });
}

/**
 * Port of compKeyFor in backend/services/lionAuto/compMatchRules.js — keep
 * the two in step. `v:<vividListingId>` when present, else the natural key
 * `n:<sectionId>|<row>|<lowSeat>|<highSeat>|<quantity>|<unitPrice>|<stockType>#<n>`
 * where n counts identical natural keys so twins stay distinct. A repeated
 * Vivid id gets the same `#n` suffix from its 2nd copy. Pass one `seen` Map
 * for every row of one payload.
 */
export function compKeyFor(comp, seen = null) {
  const c = comp || {};
  const vid = c.vividListingId;
  const hasVid = vid !== null && vid !== undefined && vid !== '';
  const base = hasVid
    ? `v:${vid}`
    : 'n:' + [c.sectionId, c.row, c.lowSeat, c.highSeat, c.quantity, c.unitPrice, c.stockType]
      .map(x => (x === null || x === undefined ? '' : String(x))).join('|');
  const n = (seen ? seen.get(base) || 0 : 0) + 1;
  if (seen) seen.set(base, n);
  if (hasVid) return n === 1 ? base : `${base}#${n}`;
  return `${base}#${n}`;
}

/**
 * Keys for a whole competitorListings array, index for index. Twins are
 * numbered in payload order over the WHOLE array, as the matcher does, so
 * key before filtering. A non-object row gets null (the matcher skips it
 * without numbering it).
 */
export function compKeysFor(competitorListings) {
  const seen = new Map();
  return (Array.isArray(competitorListings) ? competitorListings : [])
    .map(c => (c && typeof c === 'object' ? compKeyFor(c, seen) : null));
}

// The result was matched on exactly the selected sections, in any order
// (its resolved.sections, sectionId numbers, against the selection's string
// ids). A result with no resolved.sections list is not known to be.
function sameSections(result, selectedSections) {
  const resolved = result?.resolved?.sections;
  if (!Array.isArray(resolved)) return false;
  const matched = new Set(resolved.map(s => String(s?.sectionId)));
  const picked = new Set((Array.isArray(selectedSections) ? selectedSections : []).map(s => String(s?.id)));
  return matched.size === picked.size && [...matched].every(id => picked.has(id));
}

/**
 * The table's market rows, [{ key, own: false, l }]. With a group on screen
 * (`result`, its match, for exactly the selected sections) they are that
 * result's own rows, from the payload its anchor comp was picked from: every
 * comp (the anchor comp among them) and every excluded row, keyed by
 * compKey, `l` the row as sent. The page's payload is only fetched on load
 * and Refresh comps, so it can lack the anchor comp, or hold a listing sold
 * since. The server lists the no-price rows venue-wide, so those keep
 * Compare's section rule, except a zone row's; the others are the rows the
 * server put in the group's sections (SECTION rows are never listed) and,
 * when its zone fallback searched the sections' Skybox zone (no comp in the
 * sections), the zone's rows outside them: every row it lists then carries
 * scope 'zone' (isZoneRow), a no-price one too, so they are all listed
 * whatever the selection. Without a result
 * (browsing, or the match still loading), or with one for other sections
 * (its resolved.sections not the selection's set: the placeholder kept on
 * screen while the next match loads — a previous draft's, another group's,
 * or from before a section was added or removed — or an older server's with
 * none): the payload's rows keyed by compKeysFor, cut to the selection by
 * Compare's rule.
 * @param {object} [args.pricing]  useEventPricing data (competitorListings)
 * @param {object} [args.result]   the group on screen's match result, null = none
 * @param {{id, name}[]} [args.selectedSections]  the map selection (string ids); empty = whole venue
 */
export function marketRowsFor({ pricing = null, result = null, selectedSections = null } = {}) {
  const rows = [];
  if (result && sameSections(result, selectedSections)) {
    const add = (c) => {
      if (c && typeof c === 'object' && c.compKey != null) rows.push({ key: c.compKey, own: false, l: c });
    };
    for (const c of Array.isArray(result.comps) ? result.comps : []) add(c);
    for (const c of Array.isArray(result.excluded) ? result.excluded : []) {
      if (c?.reason !== 'NO_PRICE' || c?.scope === 'zone' || matchesSelectedSection(c, selectedSections)) add(c);
    }
    return rows;
  }
  const comps = pricing?.competitorListings || [];
  const keys = compKeysFor(comps);
  comps.forEach((l, i) => {
    if (keys[i] && matchesSelectedSection(l, selectedSections)) rows.push({ key: keys[i], own: false, l });
  });
  return rows;
}

/**
 * The rows with the anchor comp's (`isAnchorComp`, set by annotate) moved to
 * the front, the rest in their order: whatever the sort, it leads the market.
 * Always a new array; the input is never reordered.
 */
export function anchorFirst(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const i = list.findIndex(r => r?.isAnchorComp);
  if (i <= 0) return list.slice();
  return [list[i], ...list.slice(0, i), ...list.slice(i + 1)];
}

/**
 * The rows a table capped at `shown` renders: the first `shown`, plus the
 * anchor comp's when it sits past them (Show My Listings First with more of
 * ours than the cap), so the cap never hides it. hidden = the rows left out.
 */
export function rowsUnderCap(rows, shown) {
  if (rows.length <= shown) return { visible: rows, hidden: 0 };
  const visible = rows.slice(0, shown);
  const anchor = rows.slice(shown).find(r => r?.isAnchorComp);
  if (anchor) visible.push(anchor);
  return { visible, hidden: rows.length - visible.length };
}

/**
 * Lookups from a match result: comp key → what the group's rules made of it,
 * inventory id → member rank, inventory id → why one of ours in the group's
 * sections is not a member (MANUAL / QTY / ROW / NOTES / GROUP, as the server
 * says), inventory id → when a member newly joined the group (a saved group's
 * `joined`; a preview, or an older server, has none), and whether the split
 * ladder is on (splitLadder: our cheapest member then sets the splits comps
 * must sell in). null without a result.
 */
export function overlayFrom(result) {
  if (!result) return null;
  const comps = new Map();
  for (const c of result.comps || []) comps.set(c.compKey, { isAnchorComp: !!c.isAnchorComp });
  for (const c of result.excluded || []) comps.set(c.compKey, { reason: c.reason, detail: c.detail });
  const ranks = new Map((result.members || []).map(m => [String(m.inventoryId), m.rank]));
  const ownOut = new Map((result.membersExcluded || []).map(m => [String(m.inventoryId), { reason: m.reason, detail: m.detail }]));
  const joined = new Map((Array.isArray(result.joined) ? result.joined : [])
    .filter(j => j?.inventoryId != null)
    .map(j => [String(j.inventoryId), j.at ?? null]));
  const splitLadder = result.resolved?.validSplits?.source === 'ladder';
  return { comps, ranks, ownOut, joined, splitLadder };
}

/**
 * One row with what the group's result (overlayFrom) says about it: the
 * anchor comp (isAnchorComp), a market or own row kept out (reason, detail),
 * our cheapest member while the split ladder is on (isAnchor, "Split
 * anchor"), a member that newly joined (joinedAt). A copy when marked; a row
 * the result doesn't know (newer than it, or in another section) comes back
 * as it is.
 */
export function annotate(row, overlay) {
  if (!overlay) return row;
  if (row.own) {
    const id = String(row.l.id);
    const rank = overlay.ranks.get(id);
    if (rank !== undefined) {
      const isAnchor = rank === 0 && overlay.splitLadder;
      const joined = overlay.joined.has(id);
      if (!isAnchor && !joined) return row;
      const out = { ...row };
      if (isAnchor) out.isAnchor = true;
      if (joined) out.joinedAt = overlay.joined.get(id);
      return out;
    }
    const out = overlay.ownOut.get(id);
    return out ? { ...row, reason: out.reason, detail: out.detail } : row;
  }
  const hit = overlay.comps.get(row.key);
  if (!hit) return row;
  if (hit.reason) return { ...row, reason: hit.reason, detail: hit.detail };
  return hit.isAnchorComp ? { ...row, isAnchorComp: true } : row;
}

// ---- the zone fallback (criteria compZoneFallback) ----

/**
 * A market row the server took from the sections' Skybox zone, outside the
 * group's sections (scope 'zone': its zone fallback searched the zone). A row
 * from the page's payload carries no scope: never one.
 */
export function isZoneRow(l) {
  return l?.scope === 'zone';
}

/** The anchor comp's name: "Anchor comp (zone)" when it came from the zone, else "Anchor comp" */
export function anchorCompLabel(a) {
  return isZoneRow(a) ? 'Anchor comp (zone)' : 'Anchor comp';
}

/** The same mid-sentence (a group card): "zone anchor" / "anchor comp" */
export function anchorWord(a) {
  return isZoneRow(a) ? 'zone anchor' : 'anchor comp';
}

// resolved.zoneNames (the sections' zones, in id order): the strings,
// trimmed, blanks left out
function zoneNamesOf(resolved) {
  const names = Array.isArray(resolved?.zoneNames) ? resolved.zoneNames : [];
  return names.filter(n => typeof n === 'string').map(n => n.trim()).filter(Boolean);
}

// Two or more zone names in words: "the Floor and Lower zones", "the Floor,
// Lower and Upper zones"
const pluralZones = (names) => `the ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} zones`;

// The sentence's zone is two or more (verb agreement: "have" not "has")
const manyZones = (resolved) => zoneNamesOf(resolved).length > 1;

/**
 * The sections' zones by name: "Floor"; two or more "the Floor and Lower
 * zones"; "the same Skybox zone" when Skybox names none
 */
export function zoneNamesText(resolved) {
  const names = zoneNamesOf(resolved);
  if (names.length > 1) return pluralZones(names);
  return names.length ? names[0] : 'the same Skybox zone';
}

/** " (Floor, Lower)" to follow the word "zone"; "" when Skybox names none */
export function zoneInParens(resolved) {
  const names = zoneNamesOf(resolved);
  return names.length ? ` (${names.join(', ')})` : '';
}

/**
 * A banner's zone: "the zone (Floor)" (determiner "their": "their zone
 * (Floor)"; "the zone" when Skybox names none); two or more names "the Floor
 * and Lower zones"
 * @param {object} [resolved]          the match result's resolved
 * @param {string} [determiner='the']  'the' | 'their', for one zone
 */
export function zoneRefText(resolved, determiner = 'the') {
  const names = zoneNamesOf(resolved);
  return names.length > 1 ? pluralZones(names) : `${determiner} zone${zoneInParens(resolved)}`;
}

/**
 * The legend's Zone fallback line from a match's resolved: "Off" (or an
 * older server's, which sends no flag); "In use: priced off Floor" (the zone
 * priced the group, ZONE_COMPS); "On: Floor has no comp either" (searched,
 * none kept); "On: these sections have no zone"; "On: Floor when these
 * sections have none" (standing by: the sections have a comp). Two or more
 * zones: "the Floor and Lower zones" ("… have no comp either").
 * @param {object} [resolved]  the match result's resolved
 * @returns {string}
 */
export function zoneFallbackSummary(resolved) {
  if (resolved?.zoneFallback !== true) return 'Off';
  const zone = zoneNamesText(resolved);
  if (resolved.compScope === 'zone') return `In use: priced off ${zone}`;
  if (resolved.zoneSearched === true) return `On: ${zone} ${manyZones(resolved) ? 'have' : 'has'} no comp either`;
  if (Array.isArray(resolved.zoneIds) && resolved.zoneIds.length === 0) return 'On: these sections have no zone';
  return `On: ${zone} when these sections have none`;
}

const ZONE_OFF_HINT = 'Off: when these sections have no comp, the ladder has no anchor comp; Auto then holds the prices, or steps them down when the group has a no-comp rate.';

/**
 * The editor's hint under the zone fallback checkbox → { text, warn }. on =
 * the draft's checkbox (only true is on); resolved = its live preview's
 * (null while it loads; it can lag the checkbox by one match). warn when the
 * zone prices the group now, or was searched and has no comp either. Two or
 * more zones read "the Floor and Lower zones".
 * @param {object} [resolved]  the preview match's resolved
 * @param {boolean} on         the draft's compZoneFallback
 * @returns {{ text: string, warn: boolean }}
 */
export function zoneFallbackHint(resolved, on) {
  if (on !== true) return { text: ZONE_OFF_HINT, warn: false };
  const zone = zoneNamesText(resolved);
  const many = manyZones(resolved);
  if (resolved?.compScope === 'zone') {
    return { text: `These sections have no comp: the ladder is priced off ${zone} now.`, warn: true };
  }
  if (resolved?.zoneSearched === true) {
    return { text: `These sections have no comp and ${zone} ${many ? 'have' : 'has'} none either: the ladder has no anchor comp.`, warn: true };
  }
  if (Array.isArray(resolved?.zoneIds) && resolved.zoneIds.length === 0) {
    return { text: 'These sections have no Skybox zone, so there is nothing to fall back on.', warn: false };
  }
  const listings = many ? `the listings of ${zone}` : `${zone}'s listings`;
  return {
    text: `Only when no market listing in these sections is a comp: then the comps are ${listings} outside them, through the same filters.`,
    warn: false,
  };
}
